/**
 * Per-driver identifier quoting. Different SQL dialects use different
 * delimiters around identifiers (table / column / schema names):
 *
 *   - Postgres / Oracle / DuckDB → `"users"`
 *   - MySQL / MariaDB             → `` `users` ``
 *   - MSSQL                       → `[users]`
 *
 * Hardcoding double quotes (the Postgres style) breaks MySQL the moment
 * a name happens to be a reserved word (`user`, `key`, `order`, …) —
 * MariaDB rejects the SELECT with a syntax error before it even reaches
 * row fetching. This module centralizes the choice so callers don't
 * spread driver checks throughout the codebase.
 */

export type DriverType = 'postgresql' | 'mysql' | 'mssql' | 'oracle' | 'files' | string;

/** Quote a single identifier, escaping the close-character correctly. */
export function quoteIdentifier(name: string, driverType: DriverType): string {
  switch (driverType) {
    case 'mysql':
      return `\`${name.replace(/`/g, '``')}\``;
    case 'mssql':
      return `[${name.replace(/]/g, ']]')}]`;
    case 'oracle':
      // Oracle stores unquoted object names in UPPERCASE, and a quoted
      // reference is case-sensitive. Our introspection lowercases names for
      // display, so quoting them verbatim yields `"opera"."tbl"` which misses
      // the real `OPERA.TBL` (ORA-00942). Quote the canonical uppercase form.
      // (Objects genuinely created with quoted mixed-case names aren't
      // recoverable — introspection already folded them to lowercase.)
      return `"${name.toUpperCase().replace(/"/g, '""')}"`;
    case 'postgresql':
    case 'files':       // DuckDB driver ID, Postgres-style quoting
    default:
      return `"${name.replace(/"/g, '""')}"`;
  }
}

/**
 * Build a `schema.table` qualified reference. Drops the schema part for
 * MySQL because MySQL/MariaDB don't have schemas in the Postgres sense
 * — its "schema" IS the database, and prefixing it inside a SELECT
 * usually means cross-database access (which we'd need an explicit
 * `database` to be set up for). For now, MySQL queries use bare table
 * names and rely on the connection's default DB.
 */
export function qualifiedTable(
  schema: string | undefined,
  table: string,
  driverType: DriverType,
): string {
  if (driverType === 'mysql' || !schema) {
    return quoteIdentifier(table, driverType);
  }
  return `${quoteIdentifier(schema, driverType)}.${quoteIdentifier(table, driverType)}`;
}
